import {
  ChartBarIcon,
  DocumentIcon,
  LightBulbIcon,
  SparklesIcon,
  StarIcon,
  UserIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';

export interface UseCaseItem {
  title: string;
  description: string;
}

interface TemplateUseCasesProps {
  // Only the current template's entries, selected in getStaticProps — the
  // full templateUseCases corpus (~370KB of strings) must never be imported
  // client-side, or it ships in the page's JS chunk for every visitor.
  useCases?: UseCaseItem[];
}

const TemplateUseCases = ({ useCases }: TemplateUseCasesProps) => {
  const { t } = useLocale('tools');

  if (!useCases || useCases.length === 0) return null;

  // Array of different icons to use for each use case
  const icons = [
    <LightBulbIcon key='lightbulb' className='h-6 w-6' />,
    <UserIcon key='user' className='h-6 w-6' />,
    <DocumentIcon key='document' className='h-6 w-6' />,
    <ChartBarIcon key='chart' className='h-6 w-6' />,
    <StarIcon key='star' className='h-6 w-6' />,
    <SparklesIcon key='sparkles' className='h-6 w-6' />,
  ];

  return (
    <section className='bg-gradient-to-b from-white to-gray-50 py-16 dark:from-slate-800 dark:to-slate-900 sm:py-20'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='mx-auto max-w-2xl lg:max-w-none'>
          <div className='text-center'>
            <h2 className='text-3xl font-bold tracking-tight text-primary-600 sm:text-4xl'>
              {t('Use Cases')}
            </h2>
            <p className='mx-auto mt-4 max-w-2xl text-lg text-gray-600 dark:text-gray-300'>
              {t('Discover how this template can be used in various scenarios')}
            </p>
          </div>
          <div className='mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-3'>
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className='group relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-8 shadow-md transition-all duration-300 hover:-translate-y-1 hover:border-primary-300 hover:shadow-lg dark:border-gray-700 dark:bg-slate-800 dark:hover:border-primary-500'
              >
                <div className='mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600 transition-all duration-300 group-hover:scale-110 dark:bg-primary-900/30'>
                  {icons[index % icons.length]}
                </div>
                <h3 className='text-xl font-semibold text-gray-900 transition-colors duration-300 group-hover:text-primary-600 dark:text-white dark:group-hover:text-primary-400'>
                  {useCase.title}
                </h3>
                <div className='mt-2 h-1 w-10 rounded bg-primary-500 transition-all duration-300 group-hover:w-16'></div>
                <p className='mt-4 text-base text-gray-500 dark:text-gray-400'>
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TemplateUseCases;
